import React, { useState, useEffect } from "react";
import Head from "next/head";
import { useMemo } from "react";
import { NextPageContext } from "next";
import type { AppProps } from "next/app";
import getConfig from "next/config";
import dynamic from "next/dynamic";
import { Provider } from "react-redux";
import store from "@/redux/store";

import "normalize.css";
import "@/styles/globals.css";
import "@/styles/index.css";
import "@digital-hig/theme-data/lib/build/css/dhig-global.css";
import "@digital-hig/theme-data/lib/build/css/dhig-fonts.css";

import { themeLight } from "@digital-hig/mui/Theme";
import { createTheme, ThemeProvider } from "@mui/material/styles";

import { AppContextProvider } from "@/providers/AppContext";
import { AuthProvider } from "@/providers/AuthenticationProvider";
import { AppConfigWrapper } from "@/providers/AppConfigWrapper";
import { CloudOSDeploymentEnvironment } from "@/types/shared/CloudOSDeploymentEnvironment";
import { PageLayout } from "@/components/PageLayout";
import { HelmetProvider } from "react-helmet-async";
import backgroundImageUrl from "@/assets/images/background.png";

const BackgroundImage = () => (
  <div
    className="loginBackground"
    style={{ backgroundImage: `url(${backgroundImageUrl})`, height: "100vh" }}>
  </div>
);

const FeatureFlagProvider = dynamic(
  () => import("@/providers/FeatureFlagProvider").then((m) => ({ default: m.FeatureFlagProvider })),
  { ssr: false, loading: ({ error }) => error ? <></> : <BackgroundImage /> }
);

const App = ({ Component, pageProps }: AppProps) => {
  const [currentEnvironment, setCurrentEnvironment] = useState<CloudOSDeploymentEnvironment>("");
  const [currentClientId, setCurrentClientId] = useState<string>("");

  useEffect(() => {
    if (!window) return;
    window.userEmail = "";
    getCurrentEnv();
  }, []);

  const getCurrentEnv = () => {
    const host = window.location.origin;
    const urlString = window.location.href;
    const url = new URL(urlString);
    const urlParams = new URLSearchParams(url.search);

    if (!sessionStorage.getItem("redirectUrl")) {
      // Store full path including query params for shareable URLs
      sessionStorage.setItem("redirectUrl", url.pathname + url.search);
    }

    if (urlParams.has("cooperate") || sessionStorage.getItem("copMode")) {
      sessionStorage.setItem("copMode", "yes");
    }

    if (host.includes("dev.") || host.includes("localhost")) {
      setCurrentEnvironment("development");
      setCurrentClientId(process.env.NEXT_PUBLIC_dev_client_id || "");
      // setCurrentEnvironment("production");
      // setCurrentClientId(process.env.NEXT_PUBLIC_client_id || '')
    } else if (host.includes("stg.")) {
      setCurrentEnvironment("staging");
      setCurrentClientId(process.env.NEXT_PUBLIC_stg_client_id || "");
    } else {
      setCurrentEnvironment("production");
      setCurrentClientId(process.env.NEXT_PUBLIC_client_id || "");
    }
  };

  return (
    <React.Fragment>
      <Head>
        <title>Autodesk Publishing Platform</title>
        <meta httpEquiv="Cache-Control" content="no-cache"></meta>
        <meta httpEquiv="Pragma" content="no-cache"></meta>
        <meta httpEquiv="Cache" content="no-cache"></meta>
        <meta httpEquiv="Pragma-Control" content="no-cache"></meta>
        <meta httpEquiv="Cache-directive" content="no-cache"></meta>
        <meta httpEquiv="Pragma-directive" content="no-cache"></meta>
        <meta httpEquiv="Expires" content="0"></meta>
        <meta httpEquiv="Pragma-directive: no-cache"></meta>
        <meta httpEquiv="Cache-directive: no-cache"></meta>
        <script src="https://prd-cfp.autodesk.com/cfp-vendors/current/main.js" />
        <script src="https://prd-cfp.autodesk.com/cfp-runtime/current/main.js" />
      </Head>
      {/* <ThemeSwitcherProvider defaultTheme="light" themeMap={themes}> */}
      <AppContextProvider>
        <Provider store={store}>
          <AuthProvider clientId={currentClientId} environment={currentEnvironment}>
            <AppConfigWrapper>
            <FeatureFlagProvider environment={currentEnvironment}>
              <ThemeProvider theme={createTheme(themeLight)}>
                <HelmetProvider>
                  <PageLayout stickyHeader={true}>
                    <Component {...pageProps} />
                  </PageLayout>
                </HelmetProvider>
              </ThemeProvider>
            </FeatureFlagProvider>
            </AppConfigWrapper>
          </AuthProvider>
        </Provider>
      </AppContextProvider>
      {/* </ThemeSwitcherProvider> */}
    </React.Fragment>
  );
};

export default App;
